import { AxiosResponse } from 'axios';
import { call, put, takeLatest } from 'redux-saga/effects';
import { GET_DCF_VALUE, GET_VESSEL_DCF, GET_SUMMARY_DCF } from './dcfTypes';
import {
	getDcfValueSuccessAction, getDcfValueErrorAction,
	getVesselDcfSuccessAction, getVesselDcfErrorAction,
	getSummaryDcfSuccessAction, getSummaryDcfErrorAction
} from './dcfSlice';
import { PayloadAction } from '@reduxjs/toolkit';
import { authApiV2 } from '../../axiosWrapper';
import { 
    trackEvent,
    trackExceptionWithContext
} from '../../services/application-insights/appInsights';
import { 
    AppInsightsActions, 
    AppInsightsComponents, 
    AppInsightsErrorTypes 
} from '../../services/application-insights/appInsightsConstants';

function* getDcfValueSaga({ payload: imoList }: PayloadAction<number[] | undefined>) {
	const requestId = `dcf-value-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
	
	try {
		trackEvent(
			AppInsightsActions.DCF_VALUE_CALCULATION,
			{
				requestId,
				component: AppInsightsComponents.DCF_COMPONENT,
				imoListLength: imoList?.length || 0,
				hasImoList: !!imoList
			}
		);

		const endpoint = `/dcf-value`;

		const response: AxiosResponse<any> = yield call(authApiV2.post, endpoint, { imoList });

		trackEvent(
			AppInsightsActions.DCF_DATA_FETCH_SUCCESS,
			{
				requestId,
				component: AppInsightsComponents.DCF_COMPONENT,
				dataReceived: !!response.data?.dcfValue,
				responseSize: JSON.stringify(response.data).length
			}
		);

		yield put(getDcfValueSuccessAction(response.data));
	} catch (error) {
		trackExceptionWithContext(
			error instanceof Error ? error : new Error(String(error)),
			{
				component: AppInsightsComponents.DCF_COMPONENT,
				action: AppInsightsActions.DCF_VALUE_CALCULATION,
				additionalData: {
					requestId,
					errorType: AppInsightsErrorTypes.API_ERROR,
					imoListLength: imoList?.length || 0
				}
			}
		);
		yield put(getDcfValueErrorAction(error));
	}
}

function* getVesselDcfSaga({ payload: imoList }: PayloadAction<number[] | undefined>) {
	const requestId = `vessel-dcf-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
	
	try {
		trackEvent(
			AppInsightsActions.DCF_VESSEL_LIST_LOAD,
			{
				requestId,
				component: AppInsightsComponents.DCF_VESSEL_DCF,
				imoListLength: imoList?.length || 0,
				hasImoList: !!imoList
			}
		);

		const endpoint = `/vessel-dcf`;
		const response: AxiosResponse<any> = yield call(authApiV2.post, endpoint, { imoList });

		trackEvent(
			AppInsightsActions.DCF_DATA_FETCH_SUCCESS,
			{
				requestId,
				component: AppInsightsComponents.DCF_VESSEL_DCF,
				dataReceived: !!response.data,
				responseSize: JSON.stringify(response.data).length
			}
		);

		yield put(getVesselDcfSuccessAction(response.data));
	} catch (error) {
		trackExceptionWithContext(
			error instanceof Error ? error : new Error(String(error)),
			{
				component: AppInsightsComponents.DCF_VESSEL_DCF,
				action: AppInsightsActions.DCF_VESSEL_LIST_LOAD,
				additionalData: {
					requestId,
					errorType: AppInsightsErrorTypes.API_ERROR,
					imoListLength: imoList?.length || 0
				}
			}
		);
		yield put(getVesselDcfErrorAction(error));
	}
}

function* getSummaryDcfSaga({ payload: imoList }: PayloadAction<number[] | undefined>) {
	const requestId = `summary-dcf-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
	
	try {
		trackEvent(
			AppInsightsActions.DCF_SUMMARY_LOAD,
			{
				requestId,
				component: AppInsightsComponents.DCF_SUMMARY,
				imoListLength: imoList?.length || 0,
				hasImoList: !!imoList
			}
		);

		const endpoint = `/summary-dcf`;
		const response: AxiosResponse<any> = yield call(authApiV2.post, endpoint, { imoList });

		trackEvent(
			AppInsightsActions.DCF_DATA_FETCH_SUCCESS,
			{
				requestId,
				component: AppInsightsComponents.DCF_SUMMARY,
				dataReceived: !!response.data,
				responseSize: JSON.stringify(response.data).length
			}
		);

		yield put(getSummaryDcfSuccessAction(response.data));
	} catch (error) {
		trackExceptionWithContext(
			error instanceof Error ? error : new Error(String(error)),
			{
				component: AppInsightsComponents.DCF_SUMMARY,
				action: AppInsightsActions.DCF_SUMMARY_LOAD,
				additionalData: {
					requestId,
					errorType: AppInsightsErrorTypes.API_ERROR,
					imoListLength: imoList?.length || 0
				}
			}
		);
		yield put(getSummaryDcfErrorAction(error));
	}
}

export function* watchDcfActions(): Generator {
	yield takeLatest(GET_DCF_VALUE, getDcfValueSaga);
	yield takeLatest(GET_VESSEL_DCF, getVesselDcfSaga);
	yield takeLatest(GET_SUMMARY_DCF, getSummaryDcfSaga);
}